// frontend/src/lib/selectDecompose.js
//
// Granny-proof pricing helper for Research 1617 (Select) tier.
//
// Pure decomposition of a requested vial count into the cheapest set of the
// existing authoritative purchase options (1-vial, 3-vial kit, 5-vial kit),
// using ONLY the admin-configured Select ladder + per-product overrides.
// No hard-coded discount percentages, no assumed 3v/5v ordering, no new
// pricing engine — this is a small helper that applies the same formula
// PDP tiles and backend `_resolve_select_line_price` already use:
//
//     line_price(n) = unitPrice * n * (1 - discount[n])
//
// and picks the (a fives, b threes, c singles) combination with the lowest
// total cost such that 5a + 3b + c === totalVials. Tie-break priority:
//   1. lowest cost
//   2. fewest cart lines
//   3. larger kits first
//
// Server remains the final pricing authority — this helper only chooses
// which (variant, quantity) rows to hand to the existing cart/checkout
// path so the buyer receives the best eligible authoritative pricing
// regardless of which tile they clicked.

export function selectLinePrice(n, unitPrice, ladder) {
  const disc = Number((ladder && ladder[n]) || 0);
  const u = Number(unitPrice) || 0;
  return +(u * n * (1 - disc)).toFixed(2);
}

export function decomposeSelectQuantity(totalVials, unitPrice, ladder) {
  const total = Math.max(0, Math.floor(Number(totalVials) || 0));
  if (total <= 0) return [];
  const p5 = selectLinePrice(5, unitPrice, ladder);
  const p3 = selectLinePrice(3, unitPrice, ladder);
  const p1 = selectLinePrice(1, unitPrice, ladder);
  let best = null;
  const maxA = Math.floor(total / 5);
  for (let a = 0; a <= maxA; a++) {
    const remA = total - 5 * a;
    const maxB = Math.floor(remA / 3);
    for (let b = 0; b <= maxB; b++) {
      const c = remA - 3 * b;
      const cost = +(a * p5 + b * p3 + c * p1).toFixed(2);
      const lines = (a > 0 ? 1 : 0) + (b > 0 ? 1 : 0) + (c > 0 ? 1 : 0);
      const key = [cost, lines, -a, -b];
      if (!best || cmpArr(key, best.key) < 0) best = { a, b, c, cost, key };
    }
  }
  const out = [];
  if (best.a > 0) out.push({ variant: "5vial", n: 5, quantity: best.a });
  if (best.b > 0) out.push({ variant: "3vial", n: 3, quantity: best.b });
  if (best.c > 0) out.push({ variant: "1vial", n: 1, quantity: best.c });
  return out;
}

function cmpArr(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
}

// Total vials currently held by the (product_id, variant_mg) Select bucket.
// Ignores BYOK kits and non-Select lines by design.
export function bucketVialTotal(items, product_id, variant_mg) {
  const mg = variant_mg == null ? null : String(variant_mg);
  return (items || []).reduce((s, i) => {
    if ((i.tier || "supply") !== "select") return s;
    if (i.is_byok_kit) return s;
    if (i.product_id !== product_id) return s;
    const im = i.variant_mg == null ? null : String(i.variant_mg);
    if (im !== mg) return s;
    const n = i.variant === "5vial" ? 5 : i.variant === "3vial" ? 3 : i.variant === "1vial" ? 1 : 0;
    return s + n * (Number(i.quantity) || 0);
  }, 0);
}

export function vialCountForVariant(variant) {
  return variant === "10vial" ? 10 : variant === "5vial" ? 5 : variant === "3vial" ? 3 : variant === "1vial" ? 1 : 0;
}

// Human-readable ladder-savings preview for the PDP inline message.
// Returns { totalPrice, retailPrice, savings, message } derived entirely
// from the authoritative ladder. `null` when no ladder discount applies.
export function ladderPreview(totalVials, unitPrice, ladder) {
  const parts = decomposeSelectQuantity(totalVials, unitPrice, ladder);
  if (parts.length === 0) return null;
  const totalPrice = +parts.reduce((s, p) => s + selectLinePrice(p.n, unitPrice, ladder) * p.quantity, 0).toFixed(2);
  const retailPrice = +(Number(unitPrice) * totalVials).toFixed(2);
  const savings = +Math.max(0, retailPrice - totalPrice).toFixed(2);
  return { parts, totalPrice, retailPrice, savings };
}
